import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { Observable, debounceTime, distinctUntilChanged, firstValueFrom, map, of, switchMap } from 'rxjs';
import { ObjectAssociationType } from '../../../tokens-and-interfaces';
import { SimplifiedOpportunity } from '../../../tokens-and-interfaces';
import { CrmAssociationFormField, FormFieldAssociation } from '../interface';
import { CrmAssociationFormFieldComponent } from '../association-form-field.component';

@Component({
  selector: 'crm-sales-opportunity-association-form-field',
  templateUrl: './sales-opportunity-association.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, CrmAssociationFormFieldComponent],
})
export class CrmSalesOpportunityAssociationFormFieldComponent extends CrmAssociationFormField<ObjectAssociationType> {
  @Input({ required: true }) set accountGroupId(value: string | null) {
    this.opportunityService?.setAccountGroupId(value ?? '');
  }
  @Input({ required: true }) set associations(value: FormFieldAssociation[]) {
    this.setAssociations(value);
  }
  @Input({ transform: coerceBooleanProperty }) mobile: boolean | null = null;
  @Input() placeholder = '';

  @Output() associationsChange = new EventEmitter<FormFieldAssociation[]>();

  protected readonly crmObjectType = 'Opportunity';
  protected readonly opportunityService = this.config.services?.opportunityService;

  protected override async fetchObjects(objectIds: string[]): Promise<FormFieldAssociation[]> {
    const opportunities = this.opportunityService
      ? await firstValueFrom(this.opportunityService.getMultiOpportunity(objectIds))
      : [];
    return this.mapAssociations(opportunities);
  }

  protected override setupSearchTerm(): Observable<FormFieldAssociation[]> {
    return this.searchTerm$$.asObservable().pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((search) => (this.opportunityService ? this.opportunityService.searchOpportunities(search) : of([]))),
      map((filteredOptions) => this.mapAssociations(filteredOptions)),
    );
  }

  private mapAssociations(opportunities: SimplifiedOpportunity[]): FormFieldAssociation[] {
    return (
      opportunities?.map(
        (option) =>
          ({
            name: option.name,
            crmObjectId: option.opportunityId,
            crmObjectType: this.crmObjectType,
          }) as FormFieldAssociation,
      ) || []
    );
  }

  handleAdd(association: FormFieldAssociation): void {
    const associations = this.associationsList();
    if (!associations.find((a) => a.crmObjectId === association.crmObjectId)) {
      const newAssociations = [...associations, association];
      this.associationsList.set(newAssociations);
      this.associationsChange.emit(newAssociations);
    }
  }

  handleRemove(association: FormFieldAssociation): void {
    const associations = this.associationsList().filter((a) => a.crmObjectId !== association.crmObjectId);
    this.associationsList.set(associations);
    this.associationsChange.emit(associations);
  }

  handleSearchTermChange(searchTerm?: unknown): void {
    this.updateSearchTerm(searchTerm);
  }
}
